import axios from "axios";
import { BASE_URL } from "../api/setup";
import { useUserStore } from "../store/user-store";
import { Callback } from "../types/general";

interface LoginData {
  email: string;
  password: string;
}

interface LoginResponse {
  id: number;
  name: string;
  email_whatsapp: string;
  phone: string;
  address: string;
  token: string;
  status: string;
  access: 'USER' | 'ADMIN';
}

function useAuthModel() {
  const { setUser, clearUser, getToken, isAuthenticated } = useUserStore();

  // Login
  const login = async (
    data: LoginData,
    callback?: Callback
  ) => {
    try {
      const response = await axios.post<LoginResponse>(
        `${BASE_URL}/api/user/authentication`,
        {
          email_whatsapp: data.email,
          password: data.password,
        }
      );

      // Store user data in Zustand store
      if (response.data) {
        setUser(response.data);
      }

      callback?.response?.(response.data);
      return response.data;
    } catch (error: any) {
      console.error("Error logging in:", error);
      callback?.error?.(error);
      throw error;
    }
  };

  // Recuperação de senha
  // 1. Pede o código: o email recebe os 6 primeiros caracteres do hash e a
  //    API devolve o restante, que deve ser guardado até ao fim do fluxo.
  const requestRecovery = async (email: string): Promise<string> => {
    const response = await axios.post<{ hash_confirmation: string }>(
      `${BASE_URL}/api/user/recover`,
      { email_whatsapp: email }
    );
    return response.data.hash_confirmation;
  };

  // Reenvia o código já gerado (recuperação ou conta pendente)
  const resendCode = async (email: string): Promise<string> => {
    const response = await axios.post<{ hash_confirmation: string }>(
      `${BASE_URL}/api/user/resend`,
      null,
      { params: { email_whatsapp: email } }
    );
    return response.data.hash_confirmation;
  };

  // 2. Valida o código (código do email + restante devolvido pela API)
  const verifyRecoveryCode = async (email: string, hash: string) => {
    await axios.post(
      `${BASE_URL}/api/user/confirmation`,
      { email_whatsapp: email, hash_confirmation: hash },
      { params: { type: "RECOVER" } }
    );
  };

  // 3. Define a nova senha
  const resetPassword = async (
    email: string,
    hash: string,
    password: string
  ) => {
    await axios.post(
      `${BASE_URL}/api/user/reset-password`,
      { email_whatsapp: email, hash_confirmation: hash, password },
      { params: { type: "RECOVER" } }
    );
  };

  // Logout
  const logout = (callback?: Callback) => {
    try {
      clearUser();
      callback?.response?.(null);
    } catch (error) {
      console.error("Error logging out:", error);
      callback?.error?.(error);
    }
  };

  // Get current user
  const getCurrentUser = () => {
    return useUserStore.getState().user;
  };

  return {
    login,
    requestRecovery,
    resendCode,
    verifyRecoveryCode,
    resetPassword,
    logout,
    isAuthenticated,
    getCurrentUser,
    getToken,
  };
}

export default useAuthModel;
